import { availabilitiesAtom, itemsAtom, optionGroupsAtom } from "@/atoms";
import {
  DAYPART_DISPLAY_CAP,
  UPSELL_DISPLAY_CAP,
  findBestComboSavings,
  joinSuggestionsToCatalog,
  suggestionsSource,
  type ComboCatalog,
  type ComboSavings,
  type DaypartBand,
  type RenderableSuggestion,
  type Suggestion,
  type SuggestionCatalog,
  type UpsellSurface,
} from "@/domain/suggestions";
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog";
import { getMerchantId } from "@/lib/company-storage";
import type { Category, ItemOffer, OptionGroup } from "@/types";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";

function offerCategoryMap(
  visibleCategories: Category[],
  categoriesItemOffers: Record<string, ItemOffer[]>,
): Map<string, Pick<Category, "status" | "availabilityId">> {
  const map = new Map<string, Pick<Category, "status" | "availabilityId">>();
  for (const category of visibleCategories) {
    for (const offer of categoriesItemOffers[category.id] ?? []) {
      map.set(offer.id, category);
    }
  }
  return map;
}

/**
 * Best combo-savings card across every renderable suggestion, anchored on
 * every item already in the cart/being viewed. At most one card shows per
 * strip (the spec's "leve o combo e economize R$ X" is singular), so among
 * every anchor×suggested pair that fits a combo, the biggest saving wins.
 */
function findBestCombo(
  anchorItemIds: string[],
  suggestions: RenderableSuggestion[],
  comboCatalog: ComboCatalog,
): ComboSavings | null {
  if (suggestions.length === 0 || anchorItemIds.length === 0) return null;

  const priceByItemId = new Map<string, number>();
  for (const offer of comboCatalog.itemOffers) {
    if (!priceByItemId.has(offer.itemId)) priceByItemId.set(offer.itemId, offer.price.value);
  }

  let best: ComboSavings | null = null;
  for (const anchorItemId of anchorItemIds) {
    const anchorPrice = priceByItemId.get(anchorItemId);
    if (anchorPrice === undefined) continue;

    for (const suggestion of suggestions) {
      const suggestedPrice = suggestion.itemOffer.price.value;
      const candidate = findBestComboSavings(
        comboCatalog,
        anchorItemId,
        anchorPrice,
        suggestion.merchantItemId,
        suggestedPrice,
      );
      if (candidate && (!best || candidate.savingsCents > best.savingsCents)) best = candidate;
    }
  }

  return best;
}

/**
 * Upsell strip data for CART/ITEM surfaces: fetches the ranked ids, joins
 * them with the live catalog, and finds the best combo-savings card. `enabled
 * === false` means the merchant turned this surface off — the caller should
 * stop rendering the strip.
 *
 * Fails silent by construction: `suggestionsSource` never rejects, so a
 * network hiccup renders nothing, from a plain effect that never blocks
 * anything else on the page (no suspense, no loading gate on checkout).
 */
export function useUpsellSuggestions(
  surface: UpsellSurface,
  anchorItemIds: string[],
  excludeItemIds: string[],
  cartItemIds: ReadonlySet<string>,
): { suggestions: RenderableSuggestion[]; combo: ComboSavings | null; enabled: boolean } {
  const items = useAtomValue(itemsAtom);
  const optionGroups = useAtomValue(optionGroupsAtom);
  const availabilities = useAtomValue(availabilitiesAtom);
  const { itemOffers, visibleCategories, categoriesItemOffers } = useBrowsingChannelCatalog();

  const [enabled, setEnabled] = useState(true);
  const [rawSuggestions, setRawSuggestions] = useState<Suggestion[]>([]);
  const [exploreSlots, setExploreSlots] = useState(0);

  const anchorsKey = [...anchorItemIds].sort().join(",");
  const excludesKey = [...excludeItemIds].sort().join(",");
  const merchantId = getMerchantId();

  useEffect(() => {
    if (!merchantId || anchorItemIds.length === 0) {
      setRawSuggestions([]);
      return;
    }

    let cancelled = false;
    suggestionsSource.getUpsell(merchantId, anchorItemIds, excludeItemIds, surface, UPSELL_DISPLAY_CAP).then((response) => {
      if (cancelled) return;
      if (!response.enabled) {
        setEnabled(false);
        setRawSuggestions([]);
        return;
      }
      setRawSuggestions(response.suggestions);
      setExploreSlots(response.exploreSlots);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, surface, anchorsKey, excludesKey]);

  const categoryByOfferId = useMemo(
    () => offerCategoryMap(visibleCategories, categoriesItemOffers),
    [visibleCategories, categoriesItemOffers],
  );

  return useMemo(() => {
    const catalog: SuggestionCatalog = { itemOffers, items, optionGroups, availabilities, categoryByOfferId };
    const suggestions = joinSuggestionsToCatalog(rawSuggestions, catalog, cartItemIds, UPSELL_DISPLAY_CAP, exploreSlots);
    const combo = findBestCombo(anchorItemIds, suggestions, catalog);
    return { suggestions, combo, enabled };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSuggestions, exploreSlots, itemOffers, items, optionGroups, availabilities, categoryByOfferId, cartItemIds, anchorsKey, enabled]);
}

export type DaypartRowData = {
  band: DaypartBand | null;
  suggestions: RenderableSuggestion[];
  enabled: boolean;
};

const EMPTY_DAYPART_ROW: DaypartRowData = { band: null, suggestions: [], enabled: true };

/** Daypart row data for the home page. Same fail-silent contract as the upsell hook. */
export function useDaypartSuggestions(excludeItemIds: string[]): DaypartRowData {
  const items = useAtomValue(itemsAtom);
  const optionGroups = useAtomValue(optionGroupsAtom);
  const availabilities = useAtomValue(availabilitiesAtom);
  const { itemOffers, visibleCategories, categoriesItemOffers } = useBrowsingChannelCatalog();

  const [enabled, setEnabled] = useState(true);
  const [band, setBand] = useState<DaypartBand | null>(null);
  const [rawSuggestions, setRawSuggestions] = useState<Suggestion[]>([]);
  const [exploreSlots, setExploreSlots] = useState(0);

  const excludesKey = [...excludeItemIds].sort().join(",");
  const merchantId = getMerchantId();

  useEffect(() => {
    if (!merchantId) {
      setRawSuggestions([]);
      return;
    }

    let cancelled = false;
    suggestionsSource.getDaypart(merchantId, excludeItemIds, DAYPART_DISPLAY_CAP).then((response) => {
      if (cancelled) return;
      if (!response.enabled) {
        setEnabled(false);
        setRawSuggestions([]);
        return;
      }
      setBand(response.band);
      setRawSuggestions(response.suggestions);
      setExploreSlots(response.exploreSlots);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, excludesKey]);

  const categoryByOfferId = useMemo(
    () => offerCategoryMap(visibleCategories, categoriesItemOffers),
    [visibleCategories, categoriesItemOffers],
  );

  return useMemo(() => {
    if (rawSuggestions.length === 0) return { ...EMPTY_DAYPART_ROW, enabled };
    const catalog: SuggestionCatalog = { itemOffers, items, optionGroups, availabilities, categoryByOfferId };
    const cartItemIds = new Set(excludeItemIds);
    const suggestions = joinSuggestionsToCatalog(rawSuggestions, catalog, cartItemIds, DAYPART_DISPLAY_CAP, exploreSlots);
    return { band, suggestions, enabled };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSuggestions, exploreSlots, band, itemOffers, items, optionGroups, availabilities, categoryByOfferId, enabled, excludesKey]);
}

export type { RenderableSuggestion, ComboSavings, UpsellSurface, DaypartBand, OptionGroup };
